import React, { useContext, useRef } from 'react';
import classnames from 'classnames';
import ddt from '@wix/da-ddt';
import { PapiDeviation } from '@wix/da-papi-types';
import { useDeviationViewContext } from '../../Context';
import Full from '../../DeviationViews/Full';
import DuperbrowsePaddles from '../../Duperbrowse/DuperbrowsePaddles';
import StageBackground from '../../DeviationBackground/StageBackground';
import { SubscriptionPurchaseFTUEContext } from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import SubscriptionPurchaseFTUE from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUE';
import { useMeasuredStage } from '../hooks/useMeasuredStage';
import StageContainer from '../_partials/StageContainer';
import ZoomModalFromImageSource from '../_partials/ZoomModalFromImageSource';
import StashPaddles from '../_partials/StashPaddles';
import { DefaultArtStageProps as Props } from '../types';
import { useSelector } from 'react-redux';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';

import styles from './DesktopArtStage.scss';

// precondition: the following Desktop DeviationTypes don't end up here:
//   POLL, STATUS, COMMISSION, TIER, JOURNAL, LITERATURE (they have their own ArtStages)
// that currently leaves:
//   UNKNOWN, IMAGE, FILM, PDF

const logger = ddt.createLogger('multifile');

export const DesktopSingleArtStage: React.FC<Props> = ({
  deviation,
  zoomedImageSource,
  isZoomed,
  onEnterZoomMode,
  onExitZoomMode,
  className,
}) => {
  logger.log('DesktopSingleArtStage:', deviation.title, deviation.type);
  const { isFullscreen } = useDeviationViewContext();
  const ref = useRef<HTMLDivElement>(null);
  const { contentSize, containerSize } = useMeasuredStage(ref, !isFullscreen);
  const isDuperbrowseActive = useSelector(getIsDuperbrowseActive);

  const isSubscriptionPurchaseFTUEBeingShown = !!useContext(SubscriptionPurchaseFTUEContext);

  return (
    <StageContainer
      className={classnames(styles['root'], className, isFullscreen && styles['fullscreen'])}
      withoutInnerWrapper
    >
      <div className={styles['content']} ref={ref}>
        <StageBackground
          deviation={deviation}
          width={containerSize?.width}
          height={containerSize?.height}
        />

        <Full
          deviation={deviation}
          width={contentSize?.width}
          height={contentSize?.height}
          withoutZoomModal
          isZoomed={isZoomed}
          onEnterZoomMode={onEnterZoomMode}
          onExitZoomMode={onExitZoomMode}
        />

        {(deviation.isPublished || isDuperbrowseActive) && (
          <DuperbrowsePaddles className={styles['duperbrowse']} isInZoomModal={isZoomed} />
        )}
        {!deviation.isPublished && !isDuperbrowseActive && <StashPaddles />}
      </div>

      {zoomedImageSource && (
        <ZoomModalFromImageSource
          source={zoomedImageSource}
          allowFullviewSrcsetForPngs={(zoomedImageSource as PapiDeviation)?.isPublished ?? false}
          {...contentSize}
          isZoomed={isZoomed}
          onExitZoomMode={onExitZoomMode}
        />
      )}

      {isSubscriptionPurchaseFTUEBeingShown && (
        <SubscriptionPurchaseFTUE className={styles['subscription-purchase-ftue']} />
      )}
    </StageContainer>
  );
};
DesktopSingleArtStage.displayName = 'DesktopSingleArtStage';
export default DesktopSingleArtStage;
